import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { API_CONFIG } from '../config/api.js';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../utils/localizedPath.js';
import '../App.css';

function PublishedEventSummary() {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const navigate = useLocalizedNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailsSent, setEmailsSent] = useState({});
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => {
    fetchEventDetails();
  }, [eventId]);

  const fetchEventDetails = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(API_CONFIG.getUrl(`api/events/${eventId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch event details');
      }

      const data = await response.json();
      setEvent(data);
      setEmailsSent(data.emailsSent || {});
      setError(null);
    } catch (err) {
      console.error('Error fetching event details:', err);
      setError('Failed to load event details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmails = async (participantNames = null) => {
    try {
      setSendingEmails(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // Filter out participants without email addresses
      let targetParticipants = participantNames || givers.map(g => g.name);
      targetParticipants = targetParticipants.filter(name => {
        const participant = event.participants.find(p => p.name === name);
        return participant && participant.email && participant.email.trim() !== '';
      });

      if (targetParticipants.length === 0) {
        alert(t('publishedEvent.emailManagement.noValidEmails'));
        setSendingEmails(false);
        return;
      }

      const payload = { participantNames: targetParticipants };

      const response = await fetch(API_CONFIG.getUrl(`api/events/${eventId}/send-emails`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to send emails');
      }

      // Update emailsSent tracking
      const newEmailsSent = { ...emailsSent };
      const now = new Date().toISOString();

      targetParticipants.forEach(name => {
        newEmailsSent[name] = now;
      });

      setEmailsSent(newEmailsSent);

      // Save email tracking to backend
      const updateResponse = await fetch(API_CONFIG.getUrl(`api/events/${eventId}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          eventName: event.name,
          eventDate: event.eventDate,
          emailsSent: newEmailsSent,
        }),
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update email tracking');
      }

      alert(t('publishedEvent.emailManagement.successAlert', { count: targetParticipants.length }));
    } catch (err) {
      console.error('Error sending emails:', err);
      alert(t('publishedEvent.emailManagement.errorAlert'));
    } finally {
      setSendingEmails(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('publishedEvent.eventDetails.notSet');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatEmailDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-8">
          <p className="text-gray-500">{t('publishedEvent.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          {t('publishedEvent.backToDashboard')}
        </button>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-gray-500">{t('publishedEvent.eventNotFound')}</p>
      </div>
    );
  }

  const givers = (event.eventType === 'kris-kindle' && event.showOnlyGivers)
    ? event.participants.filter(p => p.isGiver)
    : event.participants;

  const sortedGivers = [...givers].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
        >
          <span className="mr-2">←</span> {t('publishedEvent.backToDashboard')}
        </button>
        <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-800">{event.name}</h1>
            <span className="bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded">
              {t('publishedEvent.published')}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
            <div>
              <strong>{t('publishedEvent.eventDetails.eventDate')}</strong> {formatDate(event.eventDate)}
            </div>
            <div>
              <strong>{t('publishedEvent.eventDetails.budget')}</strong> {event.budget || t('publishedEvent.eventDetails.budgetNotSpecified')}
            </div>
            <div>
              <strong>{t('publishedEvent.eventDetails.participants')}</strong> {event.participants.length}
            </div>
            <div>
              <strong>{t('publishedEvent.eventDetails.publishedDate')}</strong> {formatDate(event.publishedAt)}
            </div>
          </div>
          {event.message && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <strong className="text-sm text-gray-700">{t('publishedEvent.eventDetails.message')}</strong>
              <p className="text-sm text-gray-600 mt-1">{event.message}</p>
            </div>
          )}
        </div>
      </div>

      {/* Email Management Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-blue-900 mb-3">{t('publishedEvent.emailManagement.title')}</h2>
        <p className="text-sm text-blue-800 mb-4">
          {t('publishedEvent.emailManagement.description')}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => handleSendEmails()}
            disabled={sendingEmails}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendingEmails ? t('publishedEvent.emailManagement.sending') : t('publishedEvent.emailManagement.sendToAll')}
          </button>
          <button
            onClick={() => {
              const unsent = givers
                .filter(g => !emailsSent[g.name] && g.email && g.email.trim() !== '')
                .map(g => g.name);
              if (unsent.length > 0) {
                handleSendEmails(unsent);
              } else {
                alert(t('publishedEvent.emailManagement.allSentAlert'));
              }
            }}
            disabled={sendingEmails}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('publishedEvent.emailManagement.sendToUnsent')}
          </button>
        </div>
      </div>

      {/* Consent Status Section */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-bold text-yellow-900 mb-3">Gift Suggestions Consent Status</h2>
        <p className="text-sm text-yellow-800 mb-4">
          Track which participants have opted in to receive AI-powered gift suggestions with affiliate links.
        </p>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-3xl font-bold text-green-600">
              {event.participants.filter(p => p.consentGiven && !p.unsubscribed).length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Opted In</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-3xl font-bold text-red-600">
              {event.participants.filter(p => p.unsubscribed).length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Unsubscribed</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="text-3xl font-bold text-gray-600">
              {event.participants.filter(p => !p.consentGiven && !p.unsubscribed).length}
            </div>
            <div className="text-sm text-gray-600 mt-1">Not Responded</div>
          </div>
        </div>
      </div>

      {/* Assignments Section */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('publishedEvent.assignments.title')}</h2>
        <div className="space-y-3">
          {sortedGivers.map(giver => {
            const giverName = giver.name;
            const receiverNames = event.generatedAssignments?.[giverName] || [];
            const emailSentDate = emailsSent[giverName];
            const hasEmail = giver.email && giver.email.trim() !== '';

            return (
              <div
                key={giverName}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold mr-3">
                      {giverName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-800">{giverName}</h3>
                        {!hasEmail ? (
                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                            {t('publishedEvent.assignments.noEmail')}
                          </span>
                        ) : emailSentDate ? (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            {t('publishedEvent.assignments.sent', { date: formatEmailDate(emailSentDate) })}
                          </span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                            {t('publishedEvent.assignments.notSent')}
                          </span>
                        )}
                        {/* Consent Status Badge */}
                        {giver.consentGiven && !giver.unsubscribed && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded flex items-center gap-1">
                            <span>✓</span> Gift Suggestions
                          </span>
                        )}
                        {giver.unsubscribed && (
                          <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded flex items-center gap-1">
                            <span>⊘</span> Unsubscribed
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {t('publishedEvent.assignments.buyingFor')} <span className="font-semibold">{receiverNames.join(', ')}</span>
                      </p>
                    </div>
                  </div>
                  {hasEmail && !emailSentDate && (
                    <button
                      onClick={() => handleSendEmails([giverName])}
                      disabled={sendingEmails}
                      className="ml-4 text-sm text-blue-600 hover:text-blue-800 font-semibold disabled:opacity-50"
                    >
                      {t('publishedEvent.assignments.sendEmail')}
                    </button>
                  )}
                  {hasEmail && emailSentDate && (
                    <button
                      onClick={() => handleSendEmails([giverName])}
                      disabled={sendingEmails}
                      className="ml-4 text-sm text-gray-600 hover:text-gray-800 font-semibold disabled:opacity-50"
                    >
                      {t('publishedEvent.assignments.resend')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary Footer */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="font-semibold text-green-900 mb-2">{t('publishedEvent.summary.title')}</h3>
        <p className="text-green-800 text-sm">
          {t('publishedEvent.summary.description', { count: sortedGivers.length })}
          {Object.keys(emailsSent).length > 0 && (
            <span> {t('publishedEvent.summary.notified', { count: Object.keys(emailsSent).length })}</span>
          )}
        </p>
      </div>
    </div>
  );
}

export default PublishedEventSummary;
